import type { CSSProperties } from "react";
import { NavLink } from "react-router-dom";
import { useSession } from "../lib/session";
import { getSectionLinks, type SectionKey } from "../lib/sectionNav";
import { Breadcrumbs } from "./ui";

/** Same per-module colour scoping as AppShell's sectionAccent / Home's tileAccent. */
function sectionAccent(mod: SectionKey): CSSProperties {
  return { "--accent": `var(--mod-${mod})`, "--accent-soft": `var(--mod-${mod}-soft)` } as CSSProperties;
}

/**
 * A section's landing page: entering "Vehicles" (or any other multi-page
 * section) from Home shows this — one tile per subsection — rather than
 * dropping straight into whichever subsection used to squat the bare route.
 */
export default function SectionMenu({
  section,
  title,
  parent,
}: {
  section: SectionKey;
  title: string;
  /** The grouping section this one now sits inside, if any -- Quotes is
   * reached through Sales, Projects through Projects. Without this the
   * crumb trail says "Home / Quotes" for a page you got to in two taps,
   * and the way back up skips the group. */
  parent?: { label: string; to: string };
}) {
  const { can } = useSession();
  const links = getSectionLinks(section, can);

  return (
    <>
      <div className="page-head">
        <div>
          <Breadcrumbs
            trail={[
              { label: "Home", to: "/" },
              ...(parent ? [{ label: parent.label, to: parent.to }] : []),
              { label: title },
            ]}
          />
          <h1>{title}</h1>
        </div>
      </div>

      <div className="home-grid" style={sectionAccent(section)}>
        {links.map((l) => (
          <NavLink key={l.to} to={l.to} className="card home-tile">
            <span className="home-tile-icon" aria-hidden="true">
              {l.icon}
            </span>
            <span className="home-tile-label">{l.label}</span>
          </NavLink>
        ))}
      </div>
    </>
  );
}
